import {
  ACCOUNT_ADS_ROUTE,
  ACCOUNT_PURCHASES_ROUTE,
  ACCOUNT_SALES_ROUTE,
  ACCOUNT_SETTINGS_ROUTE,
  CHAT_ROUTE,
  FEEDBACK_ROUTE,
} from "@/common/static/routes.static";
import {
  AccountAction,
  AccountActionType,
  AccountActivity,
  AccountKpis,
  PostalLabelFailureCategory,
} from "@/features/account-dashboard/data/types";
import { OPEN_HOME_OFFERS_PATH } from "@/features/open-home/account/open-home-account";

/**
 * A translatable line. Components render it as
 * `t(copy.key, { defaultValue: copy.defaultValue, ...copy.values })`.
 * Keeping the helpers free of `t` keeps them pure and testable.
 */
export type DashboardCopy = {
  key: string;
  defaultValue: string;
  values?: Record<string, string | number>;
};

/** Formatters the page supplies (locale-aware); the helpers stay pure. */
export type DashboardFormatters = {
  /** Bids and sales in titles and activity: an exact place (`€ 450,-`). */
  price: (amount: number) => string;
  /** The earnings tiles: an accounting place (`€ 450,00`, decision 6). */
  earnings: (amount: number) => string;
  /** Locale-aware integer with thousands separators (12.152 / 12,152). */
  count: (value: number) => string;
  date: (iso: string) => string;
};

/**
 * Evelien's severity rule: blockers and errors use Attention, warnings and
 * time-bound rows use Burst, never Burst for an error. Everything else is
 * neutral.
 */
export type DashboardSeverity = "attention" | "burst" | "neutral";

export const severityFor = (type: AccountActionType): DashboardSeverity => {
  switch (type) {
    case "LABEL_FAILED":
    case "STRIPE_VERIFICATION":
      return "attention";
    case "RESPOND_TO_BID":
    case "LISTINGS_EXPIRING":
    case "DOWNLOAD_LABEL":
    case "OPEN_HOME_ADD_DETAILS":
      return "burst";
    default:
      return "neutral";
  }
};

/** A deadline in the past. `now` is injectable so the helpers stay pure. */
export const isOverdue = (action: AccountAction, now: Date): boolean =>
  action.deadline !== null &&
  new Date(action.deadline).getTime() < now.getTime();

/**
 * Round 2 (F4): a row whose deadline has passed is no longer a warning but a
 * problem, so it takes Attention whatever its type. Decision 6 still holds:
 * Burst is never used for an error, and an overdue row is one.
 */
export const severityForAction = (
  action: AccountAction,
  now: Date
): DashboardSeverity =>
  isOverdue(action, now) ? "attention" : severityFor(action.type);

export const SEVERITY_CLASSES: Record<
  DashboardSeverity,
  { icon: string; row: string }
> = {
  attention: {
    icon: "bg-attention-100 text-attention-900",
    row: "border-attention-200",
  },
  burst: { icon: "bg-burst-100 text-burst-900", row: "border-burst-300" },
  neutral: { icon: "bg-grey-50 text-grey-700", row: "border-grey-100" },
};

const ITEM_FALLBACK: DashboardCopy = {
  key: "account-dashboard:dashboard-item-fallback",
  defaultValue: "je item",
};

const itemOf = (title: string | null) => title ?? ITEM_FALLBACK.defaultValue;

export const LABEL_FAILURE_COPY: Record<
  PostalLabelFailureCategory,
  DashboardCopy
> = {
  WEIGHT: {
    key: "account-dashboard:dashboard-label-failed-weight",
    defaultValue:
      "Het opgegeven gewicht wordt niet geaccepteerd door de vervoerder.",
  },
  DIMENSIONS: {
    key: "account-dashboard:dashboard-label-failed-dimensions",
    defaultValue: "De afmetingen passen niet binnen de vervoerderslimieten.",
  },
  ADDRESS: {
    key: "account-dashboard:dashboard-label-failed-address",
    defaultValue: "Het adres van de koper kon niet worden gevalideerd.",
  },
  SERVICE_POINT: {
    key: "account-dashboard:dashboard-label-failed-service-point",
    defaultValue: "Het gekozen afhaalpunt is niet beschikbaar.",
  },
  CARRIER: {
    key: "account-dashboard:dashboard-label-failed-carrier",
    defaultValue: "De vervoerder accepteert deze zending momenteel niet.",
  },
  UNKNOWN: {
    key: "account-dashboard:dashboard-label-failed-unknown",
    defaultValue: "De vervoerder gaf geen reden op.",
  },
};

export const labelFailureCopy = (
  category: PostalLabelFailureCategory | undefined
): DashboardCopy => LABEL_FAILURE_COPY[category ?? "UNKNOWN"];

export const actionTitle = (
  action: AccountAction,
  formatters: DashboardFormatters
): DashboardCopy => {
  const item = itemOf(action.title);

  switch (action.type) {
    case "LABEL_FAILED":
      return {
        key: "account-dashboard:dashboard-action-label-failed-title",
        defaultValue: "Verzendlabel kon niet worden aangemaakt",
      };
    case "STRIPE_VERIFICATION":
      // The gateway derives the priority from the one seller account status
      // (payment settings v2): a blocker means payouts are on hold, time-bound
      // means they still run but Stripe wants something.
      return action.priority === "TIME_BOUND"
        ? {
            key: "account-dashboard:dashboard-action-stripe-title-details",
            defaultValue:
              "Vul je gegevens aan bij betaalserviceprovider Stripe",
          }
        : {
            key: "account-dashboard:dashboard-action-stripe-title",
            defaultValue:
              "Rond je verificatie bij betaalserviceprovider Stripe af",
          };
    case "RESPOND_TO_BID":
      return {
        key: "account-dashboard:dashboard-action-bid-title",
        defaultValue: "Reageer op een bod van {{amount}}",
        values: { amount: formatters.price(action.amount ?? 0) },
      };
    case "DOWNLOAD_LABEL":
      return {
        key: "account-dashboard:dashboard-action-download-label-title",
        defaultValue: "Download je verzendlabel",
      };
    case "CONFIRM_RECEIPT":
      return {
        key: "account-dashboard:dashboard-action-confirm-receipt-title",
        defaultValue: "Heb je {{item}} ontvangen?",
        values: { item },
      };
    case "CURATION_CHANGES_REQUESTED":
      return {
        key: "account-dashboard:dashboard-action-curation-title",
        defaultValue: "Pas je advertentie aan",
      };
    case "LISTINGS_EXPIRING": {
      const count = action.count ?? 1;

      return count === 1
        ? {
            key: "account-dashboard:dashboard-action-expiring-title-one",
            defaultValue: "1 advertentie verloopt binnenkort",
          }
        : {
            key: "account-dashboard:dashboard-action-expiring-title-other",
            defaultValue: "{{count}} advertenties verlopen binnenkort",
            values: { count },
          };
    }
    case "UNANSWERED_MESSAGE":
      return {
        key: "account-dashboard:dashboard-action-message-title",
        defaultValue: "Nieuw bericht over {{item}}",
        values: { item },
      };
    case "WRITE_REVIEW":
      return {
        key: "account-dashboard:dashboard-action-review-title",
        defaultValue: "Hoe was je aankoop van {{item}}?",
        values: { item },
      };
    case "OPEN_HOME_ADD_DETAILS":
      return {
        key: "account-dashboard:dashboard-action-open-home-details-title",
        defaultValue: "Vul maten en levering aan voor {{item}}",
        values: { item },
      };
  }
};

const orderNumberCopy = (orderNumber: string): DashboardCopy => ({
  key: "account-dashboard:dashboard-meta-order-number",
  defaultValue: "Bestelling {{orderNumber}}",
  values: { orderNumber },
});

const itemCopy = (title: string): DashboardCopy => ({
  key: "account-dashboard:dashboard-meta-item",
  defaultValue: "{{item}}",
  values: { item: title },
});

const overdueCopy = (
  deadline: string,
  formatters: DashboardFormatters
): DashboardCopy => ({
  key: "account-dashboard:dashboard-meta-overdue",
  defaultValue: "Deadline verstreken op {{date}}",
  values: { date: formatters.date(deadline) },
});

/**
 * A "Download label" row offers the download only while the label is usable:
 * a hosted URL exists and the send-by date has not passed (Thomas, round 5).
 * Past that date the label is treated as expired, the row says so in its meta
 * line and its button opens the order instead (round 5, F16).
 */
export const isLabelDownloadable = (
  action: AccountAction,
  now: Date = new Date()
): boolean => Boolean(action.labelUrl) && !isOverdue(action, now);

/**
 * The deadline part of a meta line: the type's own wording while the deadline
 * is ahead, one shared "deadline passed" wording once it is behind (F4).
 */
const deadlineCopy = (
  action: AccountAction,
  formatters: DashboardFormatters,
  now: Date,
  upcoming: (date: string) => DashboardCopy
): DashboardCopy | null => {
  if (!action.deadline) {
    return null;
  }

  return isOverdue(action, now)
    ? overdueCopy(action.deadline, formatters)
    : upcoming(formatters.date(action.deadline));
};

/**
 * The meta line under a row title, as an ordered list of parts. The caller
 * joins them with a separator.
 */
export const formatActionMeta = (
  action: AccountAction,
  formatters: DashboardFormatters,
  now: Date = new Date()
): DashboardCopy[] => {
  const parts: DashboardCopy[] = [];
  const pushDeadline = (upcoming: (date: string) => DashboardCopy) => {
    const part = deadlineCopy(action, formatters, now, upcoming);

    if (part) {
      parts.push(part);
    }
  };

  switch (action.type) {
    case "LABEL_FAILED":
      parts.push(labelFailureCopy(action.labelFailure?.category));

      if (action.orderNumber) {
        parts.push(orderNumberCopy(action.orderNumber));
      }

      pushDeadline(date => ({
        key: "account-dashboard:dashboard-meta-ship-before",
        defaultValue: "Verstuur voor {{date}}",
        values: { date },
      }));

      return parts;
    case "STRIPE_VERIFICATION":
      return [
        action.priority === "TIME_BOUND"
          ? {
              key: "account-dashboard:dashboard-action-stripe-meta-details",
              defaultValue:
                "Je uitbetalingen lopen gewoon door. Rond je dit niet af, dan kan Stripe ze later pauzeren.",
            }
          : {
              key: "account-dashboard:dashboard-action-stripe-meta",
              defaultValue: "Zonder verificatie kunnen we je niet uitbetalen.",
            },
      ];
    case "RESPOND_TO_BID":
      if (action.title) {
        parts.push(itemCopy(action.title));
      }

      pushDeadline(date => ({
        key: "account-dashboard:dashboard-meta-expires",
        defaultValue: "Verloopt {{date}}",
        values: { date },
      }));

      return parts;
    case "DOWNLOAD_LABEL":
      if (action.title) {
        parts.push(itemCopy(action.title));
      }

      if (action.orderNumber) {
        parts.push(orderNumberCopy(action.orderNumber));
      }

      if (action.deadline && isOverdue(action, now)) {
        // Round 5: not the generic "deadline passed" but what it means here.
        parts.push({
          key: "account-dashboard:dashboard-meta-label-expired",
          defaultValue: "Verzendlabel verlopen op {{date}}",
          values: { date: formatters.date(action.deadline) },
        });

        return parts;
      }

      pushDeadline(date => ({
        key: "account-dashboard:dashboard-meta-ship-before",
        defaultValue: "Verstuur voor {{date}}",
        values: { date },
      }));

      return parts;
    case "CONFIRM_RECEIPT":
      if (action.orderNumber) {
        parts.push(orderNumberCopy(action.orderNumber));
      }

      return parts;
    case "CURATION_CHANGES_REQUESTED":
      if (action.title) {
        parts.push(itemCopy(action.title));
      }

      parts.push({
        key: "account-dashboard:dashboard-action-curation-meta",
        defaultValue: "Onze curator vroeg om aanpassingen.",
      });

      return parts;
    case "LISTINGS_EXPIRING":
      return [
        {
          key: "account-dashboard:dashboard-action-expiring-meta",
          defaultValue: "Verleng ze om online te blijven.",
        },
      ];
    case "UNANSWERED_MESSAGE":
      pushDeadline(date => ({
        key: "account-dashboard:dashboard-meta-respond-before",
        defaultValue: "Reageer voor {{date}}",
        values: { date },
      }));

      return parts;
    case "WRITE_REVIEW":
      return [
        {
          key: "account-dashboard:dashboard-action-review-meta",
          defaultValue: "Deel je ervaring met de verkoper.",
        },
      ];
    case "OPEN_HOME_ADD_DETAILS":
      parts.push({
        key: "account-dashboard:dashboard-action-open-home-details-meta",
        defaultValue:
          "Bod van {{amount}} geaccepteerd. De koper betaalt zodra jij dit invult.",
        values: { amount: formatters.price(action.amount ?? 0) },
      });

      pushDeadline(date => ({
        key: "account-dashboard:dashboard-meta-respond-before",
        defaultValue: "Reageer voor {{date}}",
        values: { date },
      }));

      return parts;
  }
};

export const actionCtaLabel = (
  action: AccountAction,
  now: Date = new Date()
): DashboardCopy => {
  switch (action.type) {
    case "LABEL_FAILED":
      return {
        key: "account-dashboard:dashboard-cta-retry",
        defaultValue: "Probeer opnieuw",
      };
    case "STRIPE_VERIFICATION":
      return {
        key: "account-dashboard:dashboard-cta-verify",
        defaultValue: "Verificatie afronden",
      };
    case "RESPOND_TO_BID":
      return {
        key: "account-dashboard:dashboard-cta-view-bid",
        defaultValue: "Bekijk bod",
      };
    case "DOWNLOAD_LABEL":
      // Round 3 (F1): the row downloads the label itself, never a detour.
      // Round 5 (F16): without a usable label the row opens the order, and
      // the button must say so.
      if (!isLabelDownloadable(action, now)) {
        return {
          key: "account-dashboard:dashboard-cta-go-to-order",
          defaultValue: "Naar bestelling",
        };
      }

      return {
        key: "account-dashboard:dashboard-cta-download-label",
        defaultValue: "Download label",
      };
    case "CONFIRM_RECEIPT":
      return {
        key: "account-dashboard:dashboard-cta-confirm-receipt",
        defaultValue: "Ontvangst bevestigen",
      };
    case "CURATION_CHANGES_REQUESTED":
      return {
        key: "account-dashboard:dashboard-cta-edit-ad",
        defaultValue: "Advertentie bewerken",
      };
    case "LISTINGS_EXPIRING":
      return {
        key: "account-dashboard:dashboard-cta-view-ads",
        defaultValue: "Bekijk advertenties",
      };
    case "UNANSWERED_MESSAGE":
      return {
        key: "account-dashboard:dashboard-cta-reply",
        defaultValue: "Beantwoorden",
      };
    case "WRITE_REVIEW":
      return {
        key: "account-dashboard:dashboard-cta-write-review",
        defaultValue: "Review schrijven",
      };
    case "OPEN_HOME_ADD_DETAILS":
      return {
        key: "account-dashboard:dashboard-cta-open-home-details",
        defaultValue: "Details invullen",
      };
  }
};

/**
 * Buyer-side deep link. `/account/purchases/[id]` is keyed on the order GROUP
 * id, never on the order id, so a missing group id falls back to the list.
 */
export const purchaseHref = (orderGroupId: string | null): string =>
  orderGroupId
    ? `${ACCOUNT_PURCHASES_ROUTE}/${orderGroupId}`
    : ACCOUNT_PURCHASES_ROUTE;

/**
 * Where a row's CTA navigates. `null` means the CTA is an in-place action
 * (retry label, Stripe redirect) and not a link. Confirm receipt is also
 * handled in place by the row, its href is the purchase page it belongs to.
 * A row must never end up with a dead button: every link type has a fallback.
 */
export const actionHref = (
  action: AccountAction,
  now: Date = new Date()
): string | null => {
  switch (action.type) {
    case "LABEL_FAILED":
    case "STRIPE_VERIFICATION":
      return null;
    case "CONFIRM_RECEIPT":
      return purchaseHref(action.orderGroupId);
    case "RESPOND_TO_BID":
      if (action.threadId) {
        return `${CHAT_ROUTE}/${action.threadId}`;
      }

      return action.productId
        ? `${ACCOUNT_ADS_ROUTE}/${action.productId}`
        : ACCOUNT_ADS_ROUTE;
    case "DOWNLOAD_LABEL":
      // F1: the hosted label itself (opened in a new tab, like the chat's
      // "Verzendlabel openen"). The sale page is only the fallback for a
      // row the gateway returned without a label URL; `/account/sales/:id`
      // rewrites to `/account/sales?id=:id` (config/rewrites.js). Past the
      // send-by date the label counts as expired and is never offered
      // (round 5).
      if (isLabelDownloadable(action, now)) {
        return action.labelUrl;
      }

      return action.orderId
        ? `${ACCOUNT_SALES_ROUTE}/${action.orderId}`
        : ACCOUNT_SALES_ROUTE;
    case "CURATION_CHANGES_REQUESTED":
      return action.productId
        ? `${ACCOUNT_ADS_ROUTE}/${action.productId}`
        : ACCOUNT_ADS_ROUTE;
    case "LISTINGS_EXPIRING":
      // `status` is the ads list's URL filter param (useAdsStatusFilter),
      // valued with ProductStatus members.
      return `${ACCOUNT_ADS_ROUTE}?status=EXPIRING`;
    case "UNANSWERED_MESSAGE":
      return action.threadId ? `${CHAT_ROUTE}/${action.threadId}` : CHAT_ROUTE;
    case "WRITE_REVIEW":
      // Straight to the seller review form for this order (same page the
      // chat "rate seller" hint uses); the purchase list is the fallback.
      return action.orderId
        ? `${FEEDBACK_ROUTE}/${action.orderId}`
        : purchaseHref(action.orderGroupId);
    case "OPEN_HOME_ADD_DETAILS":
      // The details form sits with the offer in My Open Home.
      return OPEN_HOME_OFFERS_PATH;
  }
};

/** External (hosted) targets open in a new tab; in-app routes navigate. */
export const isExternalHref = (href: string): boolean =>
  /^https?:\/\//.test(href);

export const STRIPE_SETTINGS_PATH = `${ACCOUNT_SETTINGS_ROUTE}/payment`;

export type KpiId =
  | "activeAdsCount"
  | "openBidsCount"
  | "expectedPayout"
  | "soldThisYearCount";

export type VisibleKpi = {
  id: KpiId;
  value: number;
  kind: "count" | "money";
};

const KPI_ORDER: { id: KpiId; kind: VisibleKpi["kind"] }[] = [
  { id: "activeAdsCount", kind: "count" },
  { id: "openBidsCount", kind: "count" },
  { id: "expectedPayout", kind: "money" },
  { id: "soldThisYearCount", kind: "count" },
];

/** Tiles with value 0 are hidden; the caller hides the block when empty. */
export const visibleKpis = (kpis: AccountKpis): VisibleKpi[] =>
  KPI_ORDER.filter(({ id }) => kpis[id] > 0).map(({ id, kind }) => ({
    id,
    kind,
    value: kpis[id],
  }));

export const KPI_LABELS: Record<KpiId, DashboardCopy> = {
  activeAdsCount: {
    key: "account-dashboard:dashboard-kpi-active-ads",
    defaultValue: "Actieve advertenties",
  },
  openBidsCount: {
    key: "account-dashboard:dashboard-kpi-open-bids",
    defaultValue: "Open biedingen",
  },
  expectedPayout: {
    key: "account-dashboard:dashboard-kpi-expected-payout",
    defaultValue: "Verwachte uitbetaling",
  },
  soldThisYearCount: {
    key: "account-dashboard:dashboard-kpi-sold-this-year",
    defaultValue: "Verkocht dit jaar",
  },
};

export const activityCopy = (
  activity: AccountActivity,
  formatters: DashboardFormatters
): DashboardCopy => {
  const item = itemOf(activity.title);
  const amount = formatters.price(activity.amount ?? 0);
  /* A payout reads as on the payout tile next to it (`€ 925,00`), not as a price (WHO-5995). */
  const payout = formatters.earnings(activity.amount ?? 0);

  switch (activity.type) {
    case "ORDER_PLACED":
      return {
        key: "account-dashboard:dashboard-activity-order-placed",
        defaultValue: "{{item}} is verkocht, uitbetaling {{amount}}",
        values: { item, amount: payout },
      };
    case "PURCHASE_PLACED":
      return {
        key: "account-dashboard:dashboard-activity-purchase-placed",
        defaultValue: "Je hebt {{item}} gekocht voor {{amount}}",
        values: { item, amount },
      };
    case "ORDER_SHIPPED":
      return {
        key: "account-dashboard:dashboard-activity-order-shipped",
        defaultValue: "{{item}} is verzonden",
        values: { item },
      };
    case "ORDER_DELIVERED":
      return {
        key: "account-dashboard:dashboard-activity-order-delivered",
        defaultValue: "{{item}} is bezorgd",
        values: { item },
      };
    case "ORDER_COMPLETED":
      return {
        key: "account-dashboard:dashboard-activity-order-completed",
        defaultValue: "Bestelling {{orderNumber}} is afgerond",
        values: { orderNumber: activity.orderNumber ?? "" },
      };
    case "PAYOUT_SENT":
      return {
        key: "account-dashboard:dashboard-activity-payout-sent",
        defaultValue: "Uitbetaling van {{amount}} voor {{item}} is uitbetaald",
        values: { item, amount: payout },
      };
    case "LISTING_PUBLISHED":
      return {
        key: "account-dashboard:dashboard-activity-listing-published",
        defaultValue: "{{item}} staat online",
        values: { item },
      };
    case "LISTING_SOLD":
      return {
        key: "account-dashboard:dashboard-activity-listing-sold",
        defaultValue: "{{item}} is verkocht",
        values: { item },
      };
    case "LISTING_EXPIRED":
      return {
        key: "account-dashboard:dashboard-activity-listing-expired",
        defaultValue: "{{item}} is verlopen",
        values: { item },
      };
    case "BID_ACCEPTED":
      return {
        key: "account-dashboard:dashboard-activity-bid-accepted",
        defaultValue: "Je hebt een bod op {{item}} geaccepteerd",
        values: { item },
      };
    case "BID_REJECTED":
      return {
        key: "account-dashboard:dashboard-activity-bid-rejected",
        defaultValue: "Je hebt een bod op {{item}} afgewezen",
        values: { item },
      };
    case "LISTING_FAVORITED":
      return (activity.count ?? 1) > 1
        ? {
            key: "account-dashboard:dashboard-activity-listing-favorited-other",
            defaultValue: "{{count}} leden bewaarden {{item}}",
            values: { item, count: activity.count ?? 1 },
          }
        : {
            key: "account-dashboard:dashboard-activity-listing-favorited-one",
            defaultValue: "Iemand bewaarde {{item}}",
            values: { item },
          };
    case "FAVORITE_SOLD":
      return {
        key: "account-dashboard:dashboard-activity-favorite-sold",
        defaultValue: "{{item}} uit je favorieten is verkocht",
        values: { item },
      };
    case "FAVORITE_PRICE_LOWERED":
      return activity.previousAmount
        ? {
            key: "account-dashboard:dashboard-activity-favorite-price-lowered-from",
            defaultValue:
              "{{item}} uit je favorieten is verlaagd van {{previous}} naar {{amount}}",
            values: {
              item,
              amount,
              previous: formatters.price(activity.previousAmount),
            },
          }
        : {
            key: "account-dashboard:dashboard-activity-favorite-price-lowered",
            defaultValue:
              "{{item}} uit je favorieten is verlaagd naar {{amount}}",
            values: { item, amount },
          };
  }
};

export const VISIBLE_ACTIONS_COLLAPSED = 4;
